import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
    Calendar,
    MapPin,
    Briefcase,
    X,
    Info,
    Loader,
} from "lucide-react";
import Swal from "sweetalert2";
import {
    getMyMissionRequestsRequest,
    createMissionRequestRequest,
    cancelMissionRequestRequest,
} from "../../../../redux/actions/missionRequestActions";
import { fetchOrdreMissionActifsRequest } from "../../../../redux/actions/ordreMissionActions";
import "../../parametres/parametresStyles/ParamConges.css";

const MesOrdres = () => {
    const dispatch = useDispatch();

    // Redux state
    const { myRequests, loading } = useSelector((state) => state.missionRequests || {
        myRequests: [],
        loading: false,
    });
    const { ordresMissionActifs } = useSelector((state) => state.ordreMission || { ordresMissionActifs: [] });
    const user = useSelector((state) => state.auth.user);

    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const [formData, setFormData] = useState({
        ordreMissionId: "",
        missionNom: "",
        motif: "",
        urgence: false,
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(fetchOrdreMissionActifsRequest());
        dispatch(getMyMissionRequestsRequest());
    }, [dispatch]);

    const getCouleurByWorkflow = (workflow) => {
        const colors = {
            manager: "#3b82f6",
            admin: "#8b5cf6",
            both: "#10b981",
        };
        return colors[workflow] || "#6b7280";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const getWorkflowLabel = (workflow) => {
        const labels = {
            manager: "Validation Manager",
            admin: "Validation RH",
            both: "Manager + RH",
        };
        return labels[workflow] || workflow;
    };

    // Transformer les ordres de mission actifs pour l'affichage
    const missionsDisponibles = useMemo(() => {
        if (!ordresMissionActifs || ordresMissionActifs.length === 0) return [];
        return ordresMissionActifs.map(om => ({
            ...om,
            couleur: getCouleurByWorkflow(om.workflow),
        }));
    }, [ordresMissionActifs]);

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "Attente Manager", cls: "pcPill is-off", color: "#f59e0b" },
            PENDING_ADMIN: { label: "Attente RH", cls: "pcPill is-off", color: "#f59e0b" },
            APPROVED: { label: "Approuvé", cls: "pcPill is-on", color: "#16a34a" },
            REJECTED: { label: "Rejeté", cls: "pcPill is-off", color: "#ef4444" },
            CANCELLED: { label: "Annulé", cls: "pcPill is-off", color: "#6b7280" },
        };

        const c = config[statut] || { label: statut, cls: "pcPill", color: "#6b7280" };
        return (
            <span className={c.cls} style={{ color: c.color, backgroundColor: `${c.color}15`, fontWeight: 700 }}>
                {c.label}
            </span>
        );
    };

    const resetForm = () => {
        setFormData({
            ordreMissionId: "",
            missionNom: "",
            motif: "",
            urgence: false,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.ordreMissionId) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Veuillez sélectionner une mission",
                confirmButtonColor: "#204385",
            });
            return;
        }

        const payload = {
            ordreMissionId: Number(formData.ordreMissionId),
            motif: formData.motif || null,
            urgent: formData.urgence,
        };

        dispatch(createMissionRequestRequest(payload));
        setShowModal(false);
        resetForm();
    };

    const handleCancel = (id) => {
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment annuler cette demande ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Non",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(cancelMissionRequestRequest(id));
            }
        });
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    return (
        <div className="pcPage">
            {/* Header */}
            <div className="pcHeader">
                <div>
                    <h1>Mes demandes de mission</h1>
                    <p>Postulez aux missions disponibles et suivez vos demandes</p>
                </div>
                <button
                    className="pcBtn pcBtn--primary"
                    onClick={() => setShowModal(true)}
                >
                    <Plus size={16} />
                    Nouvelle demande
                </button>
            </div>

            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <h2 className="pcTitle">Historique des demandes</h2>
                        </div>
                        
                        {loading && myRequests.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                                <Loader size={32} className="spin" />
                            </div>
                        ) : myRequests.length === 0 ? (
                            <div className="pcEmptyCell">
                                <FileText size={38} style={{ opacity: 0.5, marginBottom: 10 }} />
                                <div>Aucune demande enregistrée</div>
                            </div>
                        ) : (
                            <div className="pcTableWrap">
                                <table className="pcTable">
                                    <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Mission</th>
                                            <th>Période</th>
                                            <th>Lieu</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {myRequests.map((d) => (
                                            <tr key={d.id}>
                                                <td>
                                                    <span className="pcCode" style={{ backgroundColor: `${getCouleurByWorkflow(d.ordreMissionWorkflow)}20`, color: getCouleurByWorkflow(d.ordreMissionWorkflow) }}>
                                                        {d.ordreMissionCode}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="pcStrong">{d.ordreMissionLibelle} {d.urgent && "🔥"}</div>
                                                    <div className="pcTiny pcMuted">Demandé le {formatDate(d.createdAt)}</div>
                                                </td>
                                                <td>
                                                    {formatDate(d.ordreMissionDateDebut)} <br/>
                                                    <span className="pcMuted pcTiny">au</span> {formatDate(d.ordreMissionDateFin)}
                                                </td>
                                                <td>
                                                    {d.ordreMissionLieu ? (
                                                        <div className="pcInline">
                                                            <MapPin size={12} />
                                                            {d.ordreMissionLieu}
                                                        </div>
                                                    ) : "—"}
                                                </td>
                                                <td>{getStatutBadge(d.status)}</td>
                                                <td>
                                                    <div className="pcRowActions">
                                                        <button
                                                            className="pcBtn pcBtn--outline"
                                                            style={{ padding: '4px 8px', fontSize: 11 }}
                                                            onClick={() => showDetails(d)}
                                                        >
                                                            Détails
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL - Nouvelle Demande */}
            {showModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Nouvelle demande de mission</div>
                                <div className="pcModal__subtitle">Sélectionnez une mission et déposez votre demande</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <form id="missionForm" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                
                                <div className="pcWizField is-full">
                                    <label>Sélectionner une mission <span className="pcReq">*</span></label>
                                    <div className="pcSelectWrap">
                                        <select
                                            className="pcInput"
                                            value={formData.ordreMissionId}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                const mission = missionsDisponibles.find(m => m.id === Number(id));
                                                setFormData({ ...formData, ordreMissionId: id, missionNom: mission ? mission.libelle : "" });
                                            }}
                                            required
                                        >
                                            <option value="">-- Choisir une mission --</option>
                                            {missionsDisponibles.map(m => (
                                                <option key={m.id} value={m.id}>
                                                    {m.code} - {m.libelle} ({formatDate(m.dateDebut)} au {formatDate(m.dateFin)})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {formData.ordreMissionId && (
                                    <div className="pcPreview" style={{ marginTop: 0 }}>
                                        {(() => {
                                            const m = missionsDisponibles.find(m => m.id === Number(formData.ordreMissionId));
                                            if (!m) return null;
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    <div style={{ fontWeight: 'bold', fontSize: 14 }}>{m.libelle}</div>
                                                    <div style={{ fontSize: 12, color: '#64748b' }}>{m.description || 'Aucune description fournie.'}</div>
                                                    
                                                    <div className="pcRecap__grid" style={{ marginTop: 8 }}>
                                                        <div className="pcRecap__item">
                                                            <div className="pcRecap__key">Période</div>
                                                            <div className="pcRecap__val">{formatDate(m.dateDebut)} - {formatDate(m.dateFin)}</div>
                                                        </div>
                                                        <div className="pcRecap__item">
                                                            <div className="pcRecap__key">Lieu</div>
                                                            <div className="pcRecap__val">{m.lieu || 'Non spécifié'}</div>
                                                        </div>
                                                        <div className="pcRecap__item">
                                                            <div className="pcRecap__key">Validation</div>
                                                            <div className="pcRecap__val">{getWorkflowLabel(m.workflow)}</div>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                                                        <div className="pcRecap__key" style={{ marginBottom: 8 }}>Frais autorisés :</div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {m.notesDeFraisAutorisees && m.notesDeFraisAutorisees.length > 0 ? (
                                                                m.notesDeFraisAutorisees.map(nf => (
                                                                    <span key={nf.id} className="pcPill" style={{ backgroundColor: '#e0e7ff', color: '#4338ca', fontSize: 11 }}>
                                                                        {nf.category} (Max: {nf.montantMaximum ? `${nf.montantMaximum} ${nf.devise}` : '∞'})
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="pcMuted pcTiny">Aucun frais autorisé</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                <div className="pcWizField is-full">
                                    <label>Motif de la demande (optionnel)</label>
                                    <textarea
                                        className="pcInput pcInput--textarea"
                                        value={formData.motif}
                                        onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                                        placeholder="Expliquez pourquoi vous souhaitez participer à cette mission..."
                                    />
                                </div>

                                <div className="pcToggleRow">
                                    <button 
                                        type="button"
                                        className={`pcToggle ${formData.urgence ? "is-on" : ""}`}
                                        onClick={() => setFormData({ ...formData, urgence: !formData.urgence })}
                                    >
                                        <div className="pcToggle__thumb"></div>
                                        <div className="pcToggle__label">Marquer comme urgent</div>
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className="pcModal__footer">
                            <button
                                type="button"
                                className="pcBtn pcBtn--ghost"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                            >
                                Annuler
                            </button>
                            <button type="submit" form="missionForm" className="pcBtn pcBtn--primary" disabled={loading}>
                                {loading ? "Envoi..." : "Soumettre la demande"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Détails de la demande */}
            {showDetailsModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Détails de la demande #{selectedDemande.id}</div>
                                <div className="pcModal__subtitle">{selectedDemande.ordreMissionLibelle}</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => setShowDetailsModal(false)}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className="pcModal__body" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>
                                {getStatutBadge(selectedDemande.status)}
                                {selectedDemande.urgent && <span className="pcPill" style={{ marginLeft: 8, backgroundColor: '#fee2e2', color: '#ef4444' }}>Urgent 🔥</span>}
                            </div>

                            <div className="pcRecap">
                                <div className="pcRecap__label">Informations de la mission</div>
                                <div className="pcRecap__grid">
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Code</div>
                                        <div className="pcRecap__val">{selectedDemande.ordreMissionCode}</div>
                                    </div>
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Date début</div>
                                        <div className="pcRecap__val">{formatDate(selectedDemande.ordreMissionDateDebut)}</div>
                                    </div>
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Date fin</div>
                                        <div className="pcRecap__val">{formatDate(selectedDemande.ordreMissionDateFin)}</div>
                                    </div>
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Lieu</div>
                                        <div className="pcRecap__val">{selectedDemande.ordreMissionLieu || "—"}</div>
                                    </div>
                                    <div className="pcRecap__item">
                                        <div className="pcRecap__key">Centre de coûts</div>
                                        <div className="pcRecap__val">{selectedDemande.ordreMissionCentreCout || "—"}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="pcWizField is-full">
                                <label className="pcMuted">Motif de la demande</label>
                                <div style={{ fontSize: 13, color: '#374151', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    {selectedDemande.motif || "Aucun motif spécifié"}
                                </div>
                            </div>

                            {(selectedDemande.managerDecision !== null || selectedDemande.adminDecision !== null) && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                                    {selectedDemande.managerDecision !== null && (
                                        <div className="pcInfoBox">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Validation Manager</div>
                                                <div style={{ fontSize: 13 }}>
                                                    <span style={{ color: selectedDemande.managerDecision ? '#16a34a' : '#ef4444', fontWeight: 600 }}>
                                                        {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                                    </span> 
                                                    {" "}par {selectedDemande.managerName || "Inconnu"} le {formatDate(selectedDemande.managerDecisionAt)}
                                                </div>
                                                {selectedDemande.managerComment && (
                                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #cbd5e1' }}>
                                                        "{selectedDemande.managerComment}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {selectedDemande.adminDecision !== null && (
                                        <div className="pcInfoBox">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Validation RH</div>
                                                <div style={{ fontSize: 13 }}>
                                                    <span style={{ color: selectedDemande.adminDecision ? '#16a34a' : '#ef4444', fontWeight: 600 }}>
                                                        {selectedDemande.adminDecision ? "Approuvé" : "Rejeté"}
                                                    </span> 
                                                    {" "}par {selectedDemande.adminName || "Inconnu"} le {formatDate(selectedDemande.adminDecisionAt)}
                                                </div>
                                                {selectedDemande.adminComment && (
                                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #cbd5e1' }}>
                                                        "{selectedDemande.adminComment}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                        <div className="pcModal__footer">
                            {(selectedDemande.status === "PENDING_MANAGER" || selectedDemande.status === "PENDING_ADMIN") && (
                                <button
                                    className="pcBtn pcBtn--outline"
                                    style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                                    onClick={() => {
                                        setShowDetailsModal(false);
                                        handleCancel(selectedDemande.id);
                                    }}
                                >
                                    Annuler la demande
                                </button>
                            )}
                            <button className="pcBtn pcBtn--ghost" onClick={() => setShowDetailsModal(false)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesOrdres;